import React, { useState, useEffect } from 'react';
import { Modal } from '../../design-system';
import { BUTTON_PRESETS } from '../../design-system/constants';
import { Form, Input, Select, message } from 'antd';
import ApiServiceNode from '../../service/ApiServiceNode';
import { departmentService } from '../../services/departmentService';
import { getApiErrorMessage } from '../../utils/errorUtils';
import logger from '../../utils/logger';



const DepartmentFormModal = ({ visible, department, onClose }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);

    useEffect(() => {
        if (visible) {
            loadUsers();
            if (department) {
                form.setFieldsValue({
                    name: department.name,
                    description: department.description,
                    email: department.email,
                    leader_id: department.leader_id,
                    sub_leader_ids: department.sub_leaders?.map(sl => sl.id) || []
                });
            } else {
                form.resetFields();
            }
        }
    }, [visible, department, form]);

    const loadUsers = async () => {
        try {
            logger.debug('🔍 Loading users...');
            const response = await ApiServiceNode.get('/user/active');
            logger.debug('📦 API Response:', response.data);

            const result = response.data.data || response.data;
            logger.debug('📊 Parsed result:', result);

            const usersList = result.users || result;
            logger.debug('👥 Users list:', usersList, 'Length:', usersList?.length);

            setUsers(usersList);
        } catch (error) {
            logger.error('❌ Error loading users:', error);
            logger.error('Error details:', error.response?.data);
            message.error('Failed to load users');
        }
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            if (department) {
                await departmentService.updateDepartment(department.id, values);
                message.success('Department updated successfully');
            } else {
                await departmentService.createDepartment(values);
                message.success('Department created successfully');
            }

            onClose(true); // Refresh list
        } catch (error) {
            if (getApiErrorMessage(error, "").includes('already exists')) {
                message.error(`Department name "${form.getFieldValue('name')}" already exists`);
            } else if (error.errorFields) {
                // Ant Design validation errors
                message.error('Please fill in all required fields');
            } else {
                message.error('Failed to save department');
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal visible={visible} onClose={() => onClose(false)} width="medium">
            <Modal.Header title={department ? 'Edit Department' : 'Add Department'} />
            <Modal.Body padding="default">
                <Form form={form} layout="vertical">
                    <Form.Item
                        label="Department Name"
                        name="name"
                        rules={[
                            { required: true, message: 'Please enter department name' },
                            { max: 32, message: 'Maximum 32 characters' }
                        ]}
                    >
                        <Input placeholder="e.g., Engineering" maxLength={32} />
                    </Form.Item>

                    <Form.Item
                        label="Description"
                        name="description"
                        rules={[{ max: 255, message: 'Maximum 255 characters' }]}
                    >
                        <Input.TextArea
                            placeholder="Department description"
                            rows={3}
                            maxLength={255}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Contact Email"
                        name="email"
                        extra="Used as the destination for automated emails about this department (e.g. HR data quality alerts CC management)."
                        rules={[
                            { type: 'email', message: 'Enter a valid email address' },
                            { max: 255, message: 'Maximum 255 characters' }
                        ]}
                    >
                        <Input
                            type="email"
                            placeholder="e.g., finance@mediatropy.com"
                            maxLength={255}
                        />
                    </Form.Item>

                    <Form.Item
                        label="Leader/Director"
                        name="leader_id"
                    >
                        <Select
                            placeholder="Select department leader"
                            allowClear
                            showSearch
                            filterOption={(input, option) =>
                                option.children.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {users.map(u => (
                                <Select.Option key={u.id} value={u.id}>
                                    {u.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label="Sub-Leaders"
                        name="sub_leader_ids"
                    >
                        <Select
                            mode="multiple"
                            placeholder="Select sub-leaders"
                            showSearch
                            filterOption={(input, option) =>
                                option.children.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {users.map(u => (
                                <Select.Option key={u.id} value={u.id}>
                                    {u.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </Modal.Body>
            <Modal.Footer buttons={[
                {
                    ...BUTTON_PRESETS.SAVE,
                    label: department ? 'Update' : 'Create',
                    loading,
                    onClick: handleSave
                }
            ]} />
        </Modal>
    );
};

export default DepartmentFormModal;
